import React from 'react';
import { Input, Button, Checkbox, Tag, message } from 'antd';
import { UserOutlined, MailOutlined, WalletOutlined } from '@ant-design/icons';
import { usePaymentProfile } from '../context/payment-profile';
import { prizesAreLive } from '../config/prizes';

const grey = { color: '#9ca3af' };

// Formato mínimo de email (*@* — algo antes y después de la arroba, sin espacios).
const EMAIL_RE = /^[^@\s]+@[^@\s]+$/;

/**
 * Field DEBE vivir FUERA de PaymentDataForm: definido adentro se recrea en cada render,
 * React desmonta/remonta el Input y el usuario pierde el foco en cada tecla (bug 2026-07-01).
 */
const Field: React.FC<{
  text: string; icon: React.ReactNode; value: string; placeholder: string;
  onChange: (v: string) => void; error?: boolean; errorMsg?: string;
  disabled?: boolean; soon?: boolean;
}> = ({ text, icon, value, placeholder, onChange, error, errorMsg, disabled, soon }) => (
  <div>
    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, color: error ? '#cf1322' : undefined }}>
      {text}
      {soon && <Tag color='default' style={{ marginLeft: 8, fontSize: 11 }}>Próximamente</Tag>}
    </div>
    <Input
      prefix={icon}
      placeholder={placeholder}
      status={error ? 'error' : undefined}
      value={value}
      disabled={disabled}
      onChange={ev => onChange(ev.target.value)}
    />
    {error && errorMsg && <div style={{ color: '#cf1322', fontSize: 12, marginTop: 4 }}>{errorMsg}</div>}
  </div>
);

/**
 * Form reusable de datos personales: Nombre, Email, Alias de Mercado Pago + checkbox de
 * Términos. Lo usan el popup ("Completa tus datos personales") y la sección de Settings
 * ("Datos Personales"). Update parcial: guarda lo cargado.
 *
 * Reglas (feedback 2026-07-13):
 *  - OBLIGATORIOS: Nombre + Email con formato *@* + Términos aceptados. Sin los 3 no se
 *    guarda (y el popup no se puede cerrar — ver payment-data-gate).
 *  - El Alias de Mercado Pago es OPCIONAL (se puede cargar más tarde).
 *  - Los términos arrancan DESTILDADOS por defecto. Una vez aceptados y persistidos, quedan
 *    tildados y bloqueados (no se des-aceptan).
 *  - Los errores en rojo aparecen recién tras el primer intento de guardar (no de entrada).
 */
export const PaymentDataForm: React.FC<{
  submitLabel?: string;
  emailLabel?: string;
  onSaved?: () => void;
}> = ({ submitLabel = 'Guardar cambios', emailLabel = 'Correo electrónico', onSaved }) => {
  const { profile, save } = usePaymentProfile();
  const [fullName, setFullName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [mpAlias, setMpAlias] = React.useState('');
  const [terms, setTerms] = React.useState(false);   // destildado por DEFECTO (feedback 2026-07-13)
  const [saving, setSaving] = React.useState(false);
  const [attempted, setAttempted] = React.useState(false);

  React.useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || '');
      setEmail(profile.email || '');
      setMpAlias(profile.mercadopago_alias || '');
      // Nunca des-tildar por el perfil: si ya los aceptó (persistido), queda tildado y locked.
      if (profile.terms_accepted) setTerms(true);
    }
  }, [profile]);

  // Requisitos OBLIGATORIOS: nombre + email válido + términos. Recién ahí se puede guardar.
  const nameOk     = !!fullName.trim();
  const emailValid = EMAIL_RE.test(email.trim());
  const canSave    = nameOk && emailValid && terms;

  const nameErr    = attempted && !nameOk;
  const emailErr   = attempted && !emailValid;
  const emailErrMsg = !email.trim() ? 'Completá tu correo electrónico.' : 'Ingresá un email válido (con @).';
  // Igual que los campos: si intentó guardar sin aceptar los términos, se marca en rojo.
  // (bug 2026-07-16: era el ÚNICO obligatorio sin feedback — con nombre+email OK, apretar
  // Aceptar no mostraba nada y el usuario no entendía por qué no podía continuar.)
  const termsErr   = attempted && !terms;

  // Los términos, una vez aceptados y persistidos en DB, quedan tildados y bloqueados.
  const termsLocked = !!profile?.terms_accepted;
  // El Alias de Mercado Pago se habilita SÓLO cuando los premios están vivos (mismo criterio
  // que el ranking → prizesAreLive). Hasta entonces se muestra "Próximamente" y bloqueado.
  const aliasEnabled = prizesAreLive();

  const handleSave = async () => {
    setAttempted(true);
    if (!canSave) return;   // falta un obligatorio → mostramos rojo y NO guardamos ni cerramos.
    setSaving(true);
    try {
      await save({ full_name: fullName, email, mercadopago_alias: mpAlias, terms_accepted: terms });
      message.success('Datos guardados');
      onSaved?.();
    } catch (e) {
      message.error((e as Error).message || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Field text='Nombre' icon={<UserOutlined style={grey} />} value={fullName}
             placeholder='Ingresa tu nombre' onChange={setFullName}
             error={nameErr} errorMsg='Completá tu nombre.' />
      <Field text={emailLabel} icon={<MailOutlined style={grey} />} value={email}
             placeholder='tu@email.com' onChange={setEmail}
             error={emailErr} errorMsg={emailErrMsg} />
      <Field text='Alias de Mercado Pago' icon={<WalletOutlined style={grey} />} value={mpAlias}
             placeholder={aliasEnabled ? 'tu.alias.mp' : 'Próximamente'} onChange={setMpAlias}
             disabled={!aliasEnabled} soon={!aliasEnabled} />

      <div>
        <Checkbox checked={terms} disabled={termsLocked} onChange={e => setTerms(e.target.checked)}
                  style={termsErr ? { color: '#cf1322' } : undefined}>
          Acepto los{' '}
          <a href='/terms' target='_blank' rel='noreferrer'>Términos y Condiciones</a>
          {' '}y la{' '}
          <a href='/privacy' target='_blank' rel='noreferrer'>Política de Privacidad</a>
        </Checkbox>
        {termsErr && (
          <div style={{ color: '#cf1322', fontSize: 12, marginTop: 4 }}>
            Tenés que aceptar los Términos y Condiciones para continuar.
          </div>
        )}
      </div>

      {/* El botón va SIEMPRE alineado a la derecha; su posición no depende de ningún estado.
          (bug 2026-07-13: antes justifyContent dependía de onCancel → el botón saltaba de lado
          al tildar/destildar los términos.) */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
        {/* El botón queda SIEMPRE habilitado: al tocarlo con datos incompletos marca en rojo
            lo que falta (guía al usuario) en vez de quedar deshabilitado sin explicación. Solo
            guarda/cierra cuando los 3 obligatorios están OK (handleSave chequea canSave). */}
        <Button type='primary' loading={saving} onClick={handleSave}>
          {submitLabel}
        </Button>
      </div>
    </div>
  );
};
